/**
 * Configuration options for the element observer
 */
interface ElementObserverOptions {
  /** Maximum time to observe in milliseconds (default: no timeout) */
  timeout?: number
  /** Root element to observe (default: document.body) */
  root?: Element
}

/**
 * Observer class for watching DOM elements
 */
export class DomWatcher {
  private config: {
    timeout: number | null
    root: Element
  }

  constructor(options: ElementObserverOptions = {}) {
    this.config = {
      timeout: options.timeout || null,
      root: options.root || document.body,
    }
  }

  /**
   * Observes the DOM for the addition of elements matching a CSS selector.
   * Stops observing after finding the first match.
   * @param selector - CSS selector to match elements (e.g., 'textarea.vfrc-chat-input', 'input[type="text"]')
   * @param callback - Function to call when element is found. Receives the element as parameter.
   * @returns Cleanup function to stop observing
   */
  onElementAddedOnce(selector: string, callback: (el: Element) => void): () => void {
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let found = false

    // Check if element already exists
    const existingElement = this.config.root.querySelector(selector)
    if (existingElement && !found) {
      found = true
      callback(existingElement)
      return () => {} // Return dummy cleanup function
    }

    // Create MutationObserver
    const observer = new MutationObserver(mutations => {
      if (found) {
        return
      }

      mutations.forEach(mutation => {
        if (found) {
          return
        }

        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            let targetElement: Element | null = null
            const asElement = node as Element

            // Check if the added node itself matches the selector
            if (typeof asElement.matches === 'function' && asElement.matches(selector)) {
              targetElement = asElement
            }
            // Check if any child of the added node matches the selector
            else if (typeof asElement.querySelector === 'function') {
              targetElement = asElement.querySelector(selector)
            }

            if (targetElement) {
              found = true
              callback(targetElement)

              observer.disconnect()
              if (timeoutId) {
                clearTimeout(timeoutId)
              }

              return
            }
          }
        })
      })
    })

    // Start observing
    observer.observe(this.config.root, {
      childList: true,
      subtree: true,
    })

    // Set timeout if specified
    if (this.config.timeout) {
      timeoutId = setTimeout(() => {
        observer.disconnect()
      }, this.config.timeout)
    }

    // Return cleanup function
    return () => {
      observer.disconnect()
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }
}
